import {
    CATEGORY_OVERVIEW_FAILURE,
    CATEGORY_OVERVIEW_REQUEST,
    CATEGORY_OVERVIEW_SUCCESS,
    COMPETITION_CATEGORY_PATCH_SUCCESS,
    COMPETITION_RESULT_PATCH_SUCCESS,
    COMPETITION_RESULT_WINNER_SUCCESS,
    CATEGORY_RESULTS_REFRESHED,
    COMPETITIONS_DELETE_DRAW_SUCCESS,
    COMPETITIONS_MAKE_DRAW_SUCCESS,
    COMPETITIONS_PATCH_SUCCESS,
    DRAW_APPROVE_SUCCESS,
    DRAW_REOPEN_SUCCESS,
    DRAW_RESET_SUCCESS,
    DRAW_SWAP_SUCCESS,
    MSAC_TIE_BREAK_SAVE_SUCCESS,
    POINTS_STAGE_RESET_SUCCESS,
    SCORE_ENTRY_SUCCESS,
    SCORE_ROUND_COMPLETE_SUCCESS,
    SCORE_ROUND_REOPEN_SUCCESS,
    SCORE_TIE_BREAK_SUCCESS,
    SHOOTING_RESULT_REMOVE_SUCCESS,
    SHOOTING_RESULT_SAVE_SUCCESS,
} from 'actions';

const initialState = {
    byKey: {},              // 'competitionId:categoryId' → overview payload
    fetching: [],            // keys with an in-flight request
    stale: [],                // keys whose cached payload is known out of date
    invalidatedInFlight: [], // keys mutated AGAIN while their revalidation request was already in flight
};

const keyOf = (competition, category) => `${competition}:${category}`;

const withoutKey = (list, key) => list.filter(k => k !== key);
const withKey = (list, key) => (list.includes(key) ? list : [...list, key]);

/**
 * The mutated category, derived from whatever the mutation action carries:
 * winner/draw-delete actions name competition+category directly; result and
 * competition-category patches carry the entity; draw-create carries the
 * created rows.
 */
const mutatedKey = action => {
    const { competition, category, result } = action;
    if (competition && category) {
        return keyOf(competition, category);
    }
    const entity = Array.isArray(result) ? result[0] : result;
    if (entity && entity.competition && entity.category) {
        return keyOf(entity.competition.id, entity.category.id);
    }
    return null;
};

/**
 * Marks one already-cached key stale, tracking whether it was ALSO mutated
 * while its revalidation request was already in flight — see the note on
 * CATEGORY_OVERVIEW_SUCCESS below for why that distinction matters.
 */
const markStale = (state, key) => {
    if (!key || !(key in state.byKey)) {
        return state;
    }
    return {
        ...state,
        stale: withKey(state.stale, key),
        invalidatedInFlight: state.fetching.includes(key)
            ? withKey(state.invalidatedInFlight, key)
            : state.invalidatedInFlight,
    };
};

/**
 * Workspace read model per category. Mutations do not patch the cached
 * overview client-side (status/counts/execution/medalists are backend-
 * derived aggregates, not values we can safely recompute in JS) — but they
 * no longer DROP it either. A mutation marks the entry STALE; the cached
 * payload keeps serving the UI (stale-while-revalidate) while
 * useCategoryOverview.js triggers a background refetch, so the active tab
 * never sees `overview` (and the draw/execution/lifecycle props derived
 * from it) go null mid-interaction. See front/docs/SPA_ARCHITECTURE_AUDIT.md
 * finding CW1.
 */
export const categoryOverviewReducer = (state = initialState, action) => {
    switch (action.type) {
        case CATEGORY_OVERVIEW_REQUEST: {
            const key = keyOf(action.competition, action.category);
            return {
                ...state,
                fetching: withKey(state.fetching, key),
                invalidatedInFlight: withoutKey(state.invalidatedInFlight, key),
            };
        }

        case CATEGORY_OVERVIEW_SUCCESS: {
            const key = keyOf(action.competition, action.category);
            // A mutation that arrived WHILE this request was in flight means
            // the response we just got may already be stale again by the
            // time it lands (it reflects the backend's state as of whenever
            // the request was SENT, not as of now) — keep `stale` set so
            // useCategoryOverview immediately fires another revalidation,
            // instead of silently going quiet on outdated data.
            const staleAgain = state.invalidatedInFlight.includes(key);
            return {
                ...state,
                byKey: { ...state.byKey, [key]: action.data },
                fetching: withoutKey(state.fetching, key),
                stale: staleAgain ? state.stale : withoutKey(state.stale, key),
                invalidatedInFlight: withoutKey(state.invalidatedInFlight, key),
            };
        }

        case CATEGORY_OVERVIEW_FAILURE: {
            const key = keyOf(action.competition, action.category);
            return {
                ...state,
                fetching: withoutKey(state.fetching, key),
                invalidatedInFlight: withoutKey(state.invalidatedInFlight, key),
            };
        }

        // Competition-level changes (e.g. marking the competition completed)
        // shift the status of EVERY category inside it — mark all of the
        // competition's cached overviews stale so each refetches in the
        // background; other competitions' caches stay untouched.
        case COMPETITIONS_PATCH_SUCCESS: {
            const competitionId = action.competition && action.competition.id;
            if (!competitionId) {
                return state;
            }
            const keys = Object.keys(state.byKey).filter(k => k.startsWith(`${competitionId}:`));
            return keys.reduce(markStale, state);
        }

        // A SHOOTING RESULT belongs to the athlete in the whole competition,
        // not to one category — every category of it may have changed whether
        // it can be confirmed. Without this the confirm panel kept the reasons
        // it read when the page opened: «Учасників без результату стрільби:
        // 33» after all 33 were in (18 Oct 2026).
        case SHOOTING_RESULT_SAVE_SUCCESS:
        case SHOOTING_RESULT_REMOVE_SUCCESS: {
            const competitionId = action.competitionId;
            if (!competitionId) {
                return state;
            }
            const keys = Object.keys(state.byKey).filter(k => k.startsWith(`${competitionId}:`));
            return keys.reduce(markStale, state);
        }

        case MSAC_TIE_BREAK_SAVE_SUCCESS:
        case COMPETITION_RESULT_WINNER_SUCCESS:
        // A mat decided a fight the bracket was watching (25 Oct 2026) — the
        // repechage ladders' finalists, the status, the bronze round come
        // from the overview, so it is re-read too.
        case CATEGORY_RESULTS_REFRESHED:
        case COMPETITION_RESULT_PATCH_SUCCESS:
        case COMPETITION_CATEGORY_PATCH_SUCCESS:
        case COMPETITIONS_MAKE_DRAW_SUCCESS:
        case COMPETITIONS_DELETE_DRAW_SUCCESS:
        case DRAW_SWAP_SUCCESS:
        case DRAW_RESET_SUCCESS:
        case DRAW_APPROVE_SUCCESS:
        case DRAW_REOPEN_SUCCESS:
        case SCORE_ENTRY_SUCCESS:
        case SCORE_TIE_BREAK_SUCCESS:
        case SCORE_ROUND_COMPLETE_SUCCESS:
        case SCORE_ROUND_REOPEN_SUCCESS:
        case POINTS_STAGE_RESET_SUCCESS: {
            return markStale(state, mutatedKey(action));
        }

        default: {
            return state;
        }
    }
};
